// src/components/apps/SUA/VendorManagement.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import api from "../../../utils/api";

export default function VendorManagement() {
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    vendor_id: "",
    vendor_name: "",
    email: "",
    is_active: true,
    notes: "",
  });
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchVendors = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = showActiveOnly ? { is_active: true } : {};
      const { data } = await api.get("/payments/vendors/", { params });
      setVendors(data || []);
      setFilteredVendors(data || []);
    } catch (err) {
      console.error("Error fetching vendors:", err);
      setError(err?.response?.data?.detail || "Failed to load vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [showActiveOnly]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredVendors(vendors);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = vendors.filter(
      (v) =>
        v.vendor_id?.toLowerCase().includes(term) ||
        v.vendor_name?.toLowerCase().includes(term) ||
        v.email?.toLowerCase().includes(term)
    );
    setFilteredVendors(filtered);
  }, [searchTerm, vendors]);

  const resetForm = () => {
    setFormData({
      vendor_id: "",
      vendor_name: "",
      email: "",
      is_active: true,
      notes: "",
    });
  };

  const handleAdd = async () => {
    try {
      await api.post("/payments/vendors/", formData);
      setAddDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create vendor");
    }
  };

  const handleEdit = async () => {
    try {
      await api.patch(`/payments/vendors/${selectedVendor.id}/`, formData);
      setEditDialogOpen(false);
      resetForm();
      setSelectedVendor(null);
      fetchVendors();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update vendor");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/payments/vendors/${selectedVendor.id}/`);
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to delete vendor");
    }
  };

  const openEditDialog = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.vendor_name,
      email: vendor.email,
      is_active: vendor.is_active,
      notes: vendor.notes || "",
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (vendor) => {
    setSelectedVendor(vendor);
    setDeleteDialogOpen(true);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", bulkFile);

    try {
      const { data } = await api.post(
        "/payments/vendors/bulk-import/",
        formDataUpload,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setBulkResult(data);
      setBulkFile(null);
      fetchVendors();
    } catch (err) {
      setError(err?.response?.data?.detail || "Bulk upload failed");
    }
  };

  const downloadTemplate = () => {
    const template = "vendor_id,vendor_name,email,is_active,notes\nV001,Example Corp,example@email.com,true,Sample vendor\n";
    const blob = new Blob([template], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vendor_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const statistics = {
    total: vendors.length,
    active: vendors.filter((v) => v.is_active).length,
    inactive: vendors.filter((v) => !v.is_active).length,
  };

  return (
    <Box component="main" sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          p: 3,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          mt: "50px",
          mb: 4,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            Vendor Management
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchVendors} disabled={loading} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<UploadIcon />}
              variant="outlined"
              onClick={() => setBulkUploadOpen(true)}
            >
              Bulk Import
            </Button>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
            >
              Add Vendor
            </Button>
          </Stack>
        </Stack>

        {/* Statistics */}
        {!loading && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">{statistics.total}</Typography>
                <Typography variant="body2">Total Vendors</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">{statistics.active}</Typography>
                <Typography variant="body2">Active Vendors</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #eb3349 0%, #f45c43 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">{statistics.inactive}</Typography>
                <Typography variant="body2">Inactive Vendors</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Search and Filter */}
        <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
          <TextField
            fullWidth
            size="small"
            placeholder="Search by vendor ID, name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
              />
            }
            label="Active Only"
          />
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Table */}
        {!loading && (
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Vendor ID
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Vendor Name
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Notes
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor) => (
                    <TableRow
                      key={vendor.id}
                      hover
                      sx={{ "&:hover": { backgroundColor: "action.hover" } }}
                    >
                      <TableCell>
                        <Chip label={vendor.vendor_id} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {vendor.vendor_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{vendor.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={vendor.is_active ? "Active" : "Inactive"}
                          size="small"
                          color={vendor.is_active ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {vendor.notes || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEditDialog(vendor)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => openDeleteDialog(vendor)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm
                          ? "No vendors match your search."
                          : "No vendors yet. Add your first vendor!"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Add Vendor Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Vendor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Vendor ID"
              required
              value={formData.vendor_id}
              onChange={(e) => setFormData({ ...formData, vendor_id: e.target.value })}
              helperText="Must match fvendno in CSV files"
            />
            <TextField
              label="Vendor Name"
              required
              value={formData.vendor_name}
              onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              helperText="This email will be used in SUA batch files"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!formData.vendor_id || !formData.vendor_name || !formData.email}
          >
            Add Vendor
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Vendor Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Vendor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Vendor ID"
              required
              disabled
              value={formData.vendor_id}
            />
            <TextField
              label="Vendor Name"
              required
              value={formData.vendor_name}
              onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete vendor <strong>{selectedVendor?.vendor_name}</strong> (
            {selectedVendor?.vendor_id})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onClose={() => setBulkUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Vendors</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Upload a CSV file with columns: <code>vendor_id</code>, <code>vendor_name</code>,{" "}
              <code>email</code>, <code>is_active</code>, <code>notes</code>
            </Alert>

            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              onClick={downloadTemplate}
              size="small"
            >
              Download Template
            </Button>

            <Button variant="outlined" component="label" fullWidth>
              {bulkFile ? `Selected: ${bulkFile.name}` : "Choose CSV File"}
              <input
                type="file"
                hidden
                accept=".csv"
                onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
              />
            </Button>

            {bulkResult && (
              <Alert severity="success">
                <Typography variant="body2">
                  Created: {bulkResult.created} | Updated: {bulkResult.updated}
                </Typography>
                {bulkResult.errors?.length > 0 && (
                  <Typography variant="caption" color="error">
                    Errors: {bulkResult.errors.join(", ")}
                  </Typography>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUploadOpen(false)}>Close</Button>
          <Button variant="contained" onClick={handleBulkUpload} disabled={!bulkFile}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
